import React, {forwardRef, KeyboardEvent, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react'

import VScroll from "../../../scrolling/custom-v-scroll/v-scroll"
import UserIcon from "../../../icons/user/user"
import EntitySelectorPane from "../../content/pane"
import {useEntitySelectorContext} from "../../context"
import {MentionOption} from "../../../editor/plugins/mentions/option"
import {findDOMNode} from "react-dom";
import PopoverMenuHeader from "../../../popover-menu/popover-menu/popover-menu-header";
import {EntityType} from "../../../../gd/const/item";

type Props = {
    style?              : any
    search              : string

    options             : MentionOption[]
}

const debug = false
// const debug = "EntitySelectorUsersList"

const EntitySelectorUsersList = forwardRef((props:Props, ref) => {
    const context = useEntitySelectorContext()

    const [currentCursorKey, setCurrentCursorKey] = useState<number>(0)
    const scrollRef = useRef<any>()

    const onKeyDown = useCallback((event:KeyboardEvent) => {
        debug && console.log(debug, 'onKeyDown', currentCursorKey, event)

        const selectOptions = props.options

        let newValue
        switch(event.code) {
            case 'Tab':
                event.preventDefault()
                context.onTab()
                break

            case 'ArrowUp':
                if (_.isNil(currentCursorKey)) {
                    newValue = 0
                } else {
                    newValue = currentCursorKey - 1
                    if (newValue < 0) newValue = 0
                }

                setCurrentCursorKey(newValue)
                break

            case 'ArrowDown':
                if (_.isNil(currentCursorKey)) {
                    newValue = 0
                } else {
                    newValue = currentCursorKey + 1
                    if (newValue > selectOptions.length - 1) newValue = selectOptions.length - 1;
                }

                setCurrentCursorKey(newValue)
                break

            case 'Enter':
                event.preventDefault()
                event.stopPropagation()

                if (!_.isNil(currentCursorKey)) {
                    const curOption = selectOptions[currentCursorKey]
                    if (curOption) context.onEntitySelected(curOption)
                }
                break
        }
        // `context` is in here because the two calls above come off it: it is a
        // fresh object every render, so leaving it out would pin this handler to
        // the one from the render it was last rebuilt on.
    },[currentCursorKey, props.options, context])

    // The component instance will be extended
    // with whatever you return from the callback passed
    // as the second argument
    useImperativeHandle(ref, () => ({
        onKeyDown,
    }))

    useEffect(()=>{
        debug && console.log(debug, 'useEffect', currentCursorKey)

        if (scrollRef.current) {
            const node = findDOMNode(scrollRef.current)
            if (node) {
                const element: any = (node as Element)?.querySelector("#option-{0}".f(currentCursorKey))
                if (element) scrollRef.current?.setScrollTop(element.offsetTop + element.clientHeight/2,true,250)
            }
        }
    }, [currentCursorKey])

    debug && console.log(debug, 'render', currentCursorKey, props)

    return (
        <EntitySelectorPane
            className   = "entity-selector-list"
            onKeyDown   = {onKeyDown}
        >
            <VScroll ref={scrollRef} minDragHeight={20}>
                {_.map(props.options, (option: MentionOption, key: number)=> {

                    const isFirst = key === 0
                    const isLast = key === props.options.length - 1 || props.options[key + 1].type === EntityType.GROUP

                    if (option.type === EntityType.GROUP) {

                        const classes = Classes("ui5-title-smaller-dark", "txt-semibold", "m-h-20", "m-b-12", {
                            "m-t-12": isFirst,
                            "m-t-20": !isFirst
                        })

                        return (
                            <div key={option.id} className={classes}>
                                {option.name}
                            </div>
                        )
                    }

                    const classes = Classes("entity-item flex-row-left-center h-36 p-l-20 p-r-20 txt-truncate", {
                        "is-highlighted": key === currentCursorKey,
                        "last-in-group" : isLast
                    })

                    return (
                        <div key        = {option.id}
                             id         = {"option-{0}".f(key)}
                             className  = {classes}
                             onClick    = {() => context.onEntitySelected(option)}
                        >
                            <UserIcon className="m-r-5" size="22" userId={option.id}/>
                            <div className="ui5-txt-default txt-truncate">{option.name}</div>
                        </div>
                    )
                })}
            </VScroll>
        </EntitySelectorPane>
    )
})

export default EntitySelectorUsersList
